/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import type { KeyboardEvent, ReactNode } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import SearchIcon from '@mui/icons-material/Search';
import Typography from '@mui/material/Typography';
import { UseFormReturn } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import {
  AutocompleteElement,
  TextFieldElement
} from 'components/muiInputWrappers';
import { validationRules } from 'helpers/utils';
import { useGlobalContext } from 'context/GlobalContext';
import type { PaginationProps } from 'hooks/usePagination';

import { FormItem } from '../FormItem';
import { FormTitle } from '../FormTitle';

export type Props = {
  children?: ReactNode;
  clearButton?: boolean;
  exportAll?: boolean;
  form?: UseFormReturn<any, any>;
  hideAddButton?: boolean;
  hideCount?: boolean;
  hideSearchButton?: boolean;
  hideSearchInput?: boolean;
  hideStatus?: boolean;
  isFromRefactor?: boolean;
  justifyContent?: string;
  md?: number;
  onSubmit?: (data?: object) => void;
  registerFindData?: number;
  renderInputODT?: boolean;
  renderInputUser?: ReactNode;
  reset?: () => void;
  searchName?: string;
  setOpenModal?: (dt: boolean) => void;
  handleResetItemsFromExcelExport?: () => void;
  submitOnChange?: boolean;
  title?: string;
  textType?: number;
} & PaginationProps;

function CatalogFilter({
  children,
  clearButton = false,
  exportAll = false,
  form,
  hideAddButton = false,
  hideCount = false,
  hideSearchButton = true,
  hideSearchInput = false,
  hideStatus = false,
  isFromRefactor = false,
  justifyContent = 'space-between',
  md = 3,
  onPageChange,
  onSubmit,
  registerFindData,
  renderInputODT = false,
  renderInputUser,
  reset,
  searchName,
  setOpenModal,
  submitOnChange = true,
  handleResetItemsFromExcelExport,
  title,
  textType
}: Props) {
  const { showError } = useGlobalContext();
  const { t } = useTranslation('global');
  const handleSubmit = () => {
    if (onPageChange) onPageChange(0);
    if (handleResetItemsFromExcelExport) {
      handleResetItemsFromExcelExport();
    }
    return form.handleSubmit((fields) => {
      const fieldsValues = fields;

      if (textType && fieldsValues?.text) {
        fieldsValues.textType = textType;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return onSubmit(fieldsValues);
    })();
  };

  const handleChangeStatus = async () => {
    await handleSubmit();
  };

  const resetSearch = () => {
    if (onPageChange) onPageChange(0);

    form.reset({});

    if (handleResetItemsFromExcelExport) {
      handleResetItemsFromExcelExport();
    }

    if (reset) reset();
  };
  const { maxLength, validateSpaces } = validationRules();

  const handleKeyDown = async (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      await handleChangeStatus();
    }
  };

  const validateContent = form.watch('search');

  const handleSpeceValidation = async () => {
    if (validateContent) {
      await handleChangeStatus();
    } else {
      showError(t('common.commonMessages.search'));
    }
  };

  const mdSize = () => {
    if (isFromRefactor) return 12;
    if (!exportAll) return md;
    return 2;
  };

  return (
    <Box>
      <Grid
        alignItems="flex-end"
        justifyContent={justifyContent}
        spacing={3}
        container
      >
        {!hideStatus && (
          <Grid md={md} xs={12} item>
            <FormTitle gutterBottom>
              {t('common.commonProperties.status')}:
            </FormTitle>
            <FormItem>
              <AutocompleteElement
                autocompleteProps={{
                  size: 'small'
                }}
                control={form.control}
                handleChange={submitOnChange && handleChangeStatus}
                name="status"
                options={[
                  {
                    id: 1,
                    label: t('common.commonProperties.active')
                  },
                  {
                    id: 2,
                    label: t('common.commonProperties.inactive')
                  }
                ]}
                matchId
              />
            </FormItem>
          </Grid>
        )}

        {!hideSearchInput && (
          <Grid md={md} xs={12} item>
            <FormTitle gutterBottom>
              {searchName ?? `${t('common.commonMessages.search')}:`}
            </FormTitle>
            <FormItem>
              <form onSubmit={handleSubmit}>
                <TextFieldElement
                  control={form.control}
                  InputProps={{
                    type: 'text',
                    endAdornment: (
                      <>
                        {form.watch('search') && (
                          <Box
                            alignItems="center"
                            display="flex"
                            justifyContent="center"
                            padding="4px"
                            sx={{
                              borderRadius: '50%',
                              '&:hover': {
                                cursor: 'pointer',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            <CloseIcon
                              fontSize="small"
                              onClick={async () => {
                                form.setValue('search', '');
                                await handleChangeStatus();
                              }}
                              sx={{
                                color: 'rgba(0, 0, 0, 0.54)'
                              }}
                              titleAccess="Clear"
                            />
                          </Box>
                        )}
                        <SearchIcon
                          onClick={async () => {
                            handleChangeStatus;
                            await handleSpeceValidation();
                          }}
                          sx={{
                            '&:hover': {
                              cursor: 'pointer'
                            }
                          }}
                          type="submit"
                        />
                      </>
                    )
                  }}
                  name="search"
                  onKeyDown={handleKeyDown}
                  size="small"
                  type="text"
                  validation={{
                    maxLength,
                    validate: validateSpaces
                  }}
                  variant="outlined"
                  fullWidth
                />
              </form>
            </FormItem>
          </Grid>
        )}
        {!renderInputODT && children}
        {clearButton && (
          <Grid md={mdSize()} xs={12} item>
            <Button
              id="clearButton"
              onClick={resetSearch}
              size="large"
              sx={{
                backgroundColor: '#FFF',
                color: '#1D6FCC',
                height: 40,
                marginTop: {
                  xs: isFromRefactor ? 0 : 2,
                  md: isFromRefactor ? 0 : 3
                }
              }}
              type="reset"
              variant="outlined"
              fullWidth
            >
              {t('common.commonProperties.clear')}
            </Button>
          </Grid>
        )}
        {!isFromRefactor && !hideAddButton && (
          <Grid md={!exportAll ? md : 2} xs={12} item>
            <Button
              onClick={() => setOpenModal(true)}
              size="large"
              sx={{
                backgroundColor: '#1D6FCC',
                color: '#FFF',
                height: 40,
                marginTop: {
                  xs: 2,
                  md: 3
                },
                padding: '5px'
              }}
              type="button"
              variant="contained"
              fullWidth
            >
              <span
                style={{
                  fontSize: '30px',
                  marginRight: 10
                }}
              >
                +
              </span>
              {title || t('common.commonProperties.newRegistration')}
            </Button>
          </Grid>
        )}
        {!hideSearchButton && (
          <Grid md={mdSize()} xs={12} item>
            <Button
              onClick={handleChangeStatus}
              size="large"
              sx={{
                backgroundColor: '#1D6FCC',
                color: '#FFF',
                height: 40,
                marginTop: {
                  xs: isFromRefactor ? 0 : 2,
                  md: isFromRefactor ? 0 : 3
                }
              }}
              type="submit"
              variant="contained"
              fullWidth
            >
              {t('common.commonProperties.search')}
            </Button>
          </Grid>
        )}
        {!isFromRefactor && !hideCount && (
          <Grid
            marginY="14px"
            xs={renderInputODT || renderInputUser ? 6 : 12}
            item
          >
            <Typography fontWeight={500}>
              {t('common.commonProperties.logsFound')?.replace(
                '{{number}}',
                `${registerFindData || '0'}`
              )}
            </Typography>
          </Grid>
        )}
        {renderInputODT && (
          <Grid display="flex" justifyContent="flex-end" xs={6} item>
            {children || renderInputUser}
          </Grid>
        )}
        {!isFromRefactor && renderInputUser && (
          <Grid display="flex" justifyContent="flex-end" xs={6} item>
            {renderInputUser}
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default CatalogFilter;
